import React, { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { TextField } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnMaskBoxProps {
  defaultValue?: string;
  onValueChanged?: (value: string) => void;
  format?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

const SnMaskBox = React.memo(
  ({
    defaultValue = "",
    onValueChanged,
    format = "",
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnMaskBoxProps) => {
    const [value, setValue] = useState(defaultValue);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");
    // 값이 없을 때 입력 형식을 안내한다. 예) "###-##-#####" → "000-00-00000"
    // '@' 같은 글리프는 숫자보다 폭/높이가 커서 폰트가 다르게 보이므로, 실제 값과 같은 크기로 보이는 '0'을 쓴다.
    const placeholder = format ? format.replace(/#/g, "0") : undefined;

    const fetchDefaultValue = async () => {
      try {
        const newValue = defaultValue ? defaultValue : "";
        if (value !== newValue) {
          setValue(newValue);
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    const handleValueChange = (values: any) => {
      setValue(values.value || "");
      //setValue(values.floatValue || 0); // floatValue로 숫자 관리
    };

    const handleBlur = () => {
      if (onValueChanged) {
        onValueChanged(value); // 포커스가 빠졌을 때만 호출
      }
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <PatternFormat
        value={value}
        customInput={TextField}
        format={format}
        mask="_"
        placeholder={placeholder}
        onValueChange={handleValueChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        fullWidth
        sx={{
          "& input": { textAlign: "right" },
          backgroundColor: backgroundColor,
          fontSize: 12,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderWidth: borderWidth, // 테두리 두께 설정
            },
            "&:hover fieldset": {
              borderWidth: borderWidth, // 호버 시 두께 유지
            },
            "&.Mui-focused fieldset": {
              borderWidth: borderWidth, // 포커스 시 두께 유지
            },
          },
        }}
        slotProps={{
          input: {
            inputRef: inputRef,
            style: {
              height: StyleSettings.control_height,
              fontSize: StyleSettings.font,
            },
          },
        }}
        disabled={isReadOnly}
      />
    );
  }
);

export default SnMaskBox;
